import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import Sidebar from './Sidebar';
import Header from './Header';
import KelolaAkun from './KelolaAkun';
import './Dashboard.css';
import 'bootstrap-icons/font/bootstrap-icons.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
const WS_URL = process.env.REACT_APP_WS_URL || 'ws://localhost:5000';

function Dashboard({ user, onLogout }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [users, setUsers] = useState([]);
  const [showRegisterForm, setShowRegisterForm] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(window.innerWidth > 768);
  const [formData, setFormData] = useState({
    nama: '',
    no_telp: '',
    password: '',
    role: 'kurir'
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [pengirimanStats, setPengirimanStats] = useState({
    total: 0,
    dalamproses: 0,
    dikirim: 0,
    diterima: 0
  });
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const reconnectAttemptsRef = useRef(0);
  const isAuthenticatedRef = useRef(false);
  const MAX_RECONNECT_ATTEMPTS = 5;

  const getCurrentPage = () => {
    const path = location.pathname;
    
    if (path.includes('/kelolaakun')) return 'kelolaakun';
    if (path.includes('/ringkasan')) return 'ringkasan-pengiriman';
    if (path.includes('/users')) return 'users';
    
    return 'dashboard';
  };

  const fetchUsers = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(response.data.users);
    } catch (err) {
      console.error('Error fetching users:', err);
    }
  }, []);

  const fetchPengirimanStats = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/pengiriman`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const data = response.data.data;
      const stats = {
        total: data.length,
        dalamproses: data.filter(p => p.status === 'dalamproses').length,
        dikirim: data.filter(p => p.status === 'dikirim').length,
        diterima: data.filter(p => p.status === 'diterima').length
      };
      
      setPengirimanStats(stats);
    } catch (err) {
      console.error('Error fetching pengiriman stats:', err);
    }
  }, []);

  const connectWebSocket = useCallback(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      console.log('No token found, skipping WebSocket connection');
      return;
    }

    if (wsRef.current) {
      const state = wsRef.current.readyState;
      if (state === WebSocket.CONNECTING || state === WebSocket.OPEN) {
        console.log('WebSocket already connected or connecting');
        return;
      }
    }

    if (reconnectAttemptsRef.current >= MAX_RECONNECT_ATTEMPTS) {
      console.log('Max reconnection attempts reached');
      return;
    }

    try {
           const ws = new WebSocket(WS_URL);

      ws.onopen = () => {
              reconnectAttemptsRef.current = 0;
        isAuthenticatedRef.current = false;
        ws.send(JSON.stringify({ type: 'authenticate', token }));
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          
          if (data.type === 'authenticated') {
                       isAuthenticatedRef.current = true;
          } else if (data.type === 'online_users') {
            setUsers(data.users);
          }
        } catch (err) {
          console.error('Error parsing message:', err);
        }
      };

      ws.onerror = (error) => {
        console.error('WebSocket error:', error);
      };

      ws.onclose = (event) => {
        console.log(`WebSocket closed with code: ${event.code}`);
        wsRef.current = null;
        isAuthenticatedRef.current = false;
        
        if (reconnectTimeoutRef.current) {
          clearTimeout(reconnectTimeoutRef.current);
          reconnectTimeoutRef.current = null;
        }
        
        const shouldReconnect = 
          event.code !== 1000 && 
          localStorage.getItem('token') &&
          reconnectAttemptsRef.current < MAX_RECONNECT_ATTEMPTS;
        
        if (shouldReconnect) {
          reconnectAttemptsRef.current += 1;
          const delay = Math.min(1000 * Math.pow(2, reconnectAttemptsRef.current), 10000);
          console.log(`Reconnecting in ${delay}ms (attempt ${reconnectAttemptsRef.current}/${MAX_RECONNECT_ATTEMPTS})...`);
          
          reconnectTimeoutRef.current = setTimeout(() => {
            reconnectTimeoutRef.current = null;
            connectWebSocket();
          }, delay);
        }
      };

      wsRef.current = ws;
    } catch (err) {
      console.error('WebSocket init error:', err);
      wsRef.current = null;
    }
  }, []);

  useEffect(() => {
    fetchUsers();
    fetchPengirimanStats();
    
    const wsTimeout = setTimeout(() => {
      connectWebSocket();
    }, 500);

    return () => {
      clearTimeout(wsTimeout);
      
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
      
      if (wsRef.current) {
        const ws = wsRef.current;
        ws.onclose = null;
        if (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING) {
          ws.close(1000, 'Component unmounted');
        }
        wsRef.current = null;
      }
      
      reconnectAttemptsRef.current = 0;
      isAuthenticatedRef.current = false;
    };
  }, [fetchUsers, fetchPengirimanStats, connectWebSocket]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/api/register`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setSuccess('User berhasil didaftarkan!');
      setFormData({ nama: '', no_telp: '', password: '', role: 'kurir' });
      setShowRegisterForm(false);
      fetchUsers();
      
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.message || 'Registrasi gagal');
    }
  };

  const formatLastSeen = (lastSeen) => {
    const date = new Date(lastSeen);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 1) return 'Baru saja';
    if (diffMins < 60) return `${diffMins} menit yang lalu`;
    
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours} jam yang lalu`;
    
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    
    return `${hours}.${minutes} ${day}/${month}/${year}`;
  };

  const handleNavigate = (page) => {
    switch (page) {
      case 'dashboard':
        navigate('/dashboard');
        break;
      case 'pengiriman':
        navigate('/pengiriman');
        break;
      case 'status-pengiriman':
        navigate('/status-pengiriman');
        break;
      case 'kelolaakun':
        navigate('/kelolaakun');
        break;
      default:
        navigate('/dashboard');
    }
  };

  const handleToggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const renderContent = () => {
    const currentPage = getCurrentPage();
    
    switch (currentPage) {
      case 'kelolaakun':
        return <KelolaAkun users={users} onRefresh={fetchUsers} formatLastSeen={formatLastSeen} />;
      case 'ringkasan-pengiriman':
        return (
          <div className="page-content">
            <h2>Ringkasan Pengiriman</h2>
            <p>Halaman ringkasan pengiriman (Coming Soon)</p>
          </div>
        );
      case 'users':
        return renderUsersContent();
      default:
        return renderDashboardContent();
    }
  };

  const renderDashboardContent = () => (
    <div className="page-content">
      <div className="dashboard-header">
        <h2>
          <i className="bi bi-speedometer2 me-2"></i>
          Dashboard Overview
        </h2>
        <p className="dashboard-subtitle">Selamat datang, {user.nama}!</p>
      </div>
      
      <div className="stats-section">
        <h3 className="stats-title">
          <i className="bi bi-people-fill me-2"></i>
          Statistik User
        </h3>
        <div className="dashboard-stats">
          <div className="stat-card">
            <div className="stat-icon" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
              <i className="bi bi-people-fill"></i>
            </div>
            <div className="stat-info">
              <h3>Total Users</h3>
              <p className="stat-number">{users.length}</p>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon" style={{ background: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)' }}>
              <i className="bi bi-circle-fill text-success"></i>
            </div>
            <div className="stat-info">
              <h3>Online</h3>
              <p className="stat-number">{users.filter(u => u.is_online).length}</p>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon" style={{ background: 'linear-gradient(135deg, #868f96 0%, #596164 100%)' }}>
              <i className="bi bi-circle-fill text-secondary"></i>
            </div>
            <div className="stat-info">
              <h3>Offline</h3>
              <p className="stat-number">{users.filter(u => !u.is_online).length}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="stats-section" style={{ marginTop: '30px' }}>
        <h3 className="stats-title">
          <i className="bi bi-box-seam-fill me-2"></i>
          Statistik Pengiriman
        </h3>
        <div className="dashboard-stats">
          <div className="stat-card">
            <div className="stat-icon" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
              <i className="bi bi-box-seam-fill"></i>
            </div>
            <div className="stat-info">
              <h3>Total Pengiriman</h3>
              <p className="stat-number">{pengirimanStats.total}</p>
            </div>
          </div>
          <div className="stat-card status-dalamproses">
            <div className="stat-icon" style={{ background: 'linear-gradient(135deg, #ffc107 0%, #ff9800 100%)' }}>
              <i className="bi bi-hourglass-split"></i>
            </div>
            <div className="stat-info">
              <h3>Dalam Proses</h3>
              <p className="stat-number">{pengirimanStats.dalamproses}</p>
            </div>
          </div>
          <div className="stat-card status-dikirim">
            <div className="stat-icon" style={{ background: 'linear-gradient(135deg, #0d6efd 0%, #0a58ca 100%)' }}>
              <i className="bi bi-truck"></i>
            </div>
            <div className="stat-info">
              <h3>Dikirim</h3>
              <p className="stat-number">{pengirimanStats.dikirim}</p>
            </div>
          </div>
          <div className="stat-card status-diterima">
            <div className="stat-icon" style={{ background: 'linear-gradient(135deg, #198754 0%, #157347 100%)' }}>
              <i className="bi bi-check-circle-fill"></i>
            </div>
            <div className="stat-info">
              <h3>Diterima</h3>
              <p className="stat-number">{pengirimanStats.diterima}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderUsersContent = () => (
    <>
      {success && (
        <div className="success-message">
          <i className="bi bi-check-circle me-2"></i>
          {success}
        </div>
      )}
      {error && (
        <div className="error-message">
          <i className="bi bi-x-circle me-2"></i>
          {error}
        </div>
      )}

      {user.role === 'admin' && (
        <div className="admin-actions">
          <button 
            onClick={() => setShowRegisterForm(!showRegisterForm)} 
            className="add-user-button"
          >
            <i className={`bi ${showRegisterForm ? 'bi-x-circle' : 'bi-plus-circle'} me-2`}></i>
            {showRegisterForm ? 'Tutup Form' : 'Tambah User Baru'}
          </button>
        </div>
      )}

      {showRegisterForm && user.role === 'admin' && (
        <div className="register-form-container">
          <h2>
            <i className="bi bi-person-plus-fill me-2"></i>
            Tambah User Baru
          </h2>
          <form onSubmit={handleRegister} className="register-form">
            <div className="form-row">
              <div className="form-group">
                <label>
                  <i className="bi bi-person me-2"></i>
                  Nama
                </label>
                <input
                  type="text"
                  name="nama"
                  value={formData.nama}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="form-group">
                <label>
                  <i className="bi bi-telephone me-2"></i>
                  No Telepon
                </label>
                <input
                  type="text"
                  name="no_telp"
                  value={formData.no_telp}
                  onChange={handleChange}
                  required
                />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>
                  <i className="bi bi-lock me-2"></i>
                  Password
                </label>
                <input
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="form-group">
                <label>
                  <i className="bi bi-shield-check me-2"></i>
                  Role
                </label>
                <select name="role" value={formData.role} onChange={handleChange}>
                  <option value="admin">Admin</option>
                  <option value="kurir">Kurir</option>
                </select>
              </div>
            </div>
            <button type="submit" className="submit-button">
              <i className="bi bi-save me-2"></i>
              Daftarkan User
            </button>
          </form>
        </div>
      )}

      <div className="users-section">
        <h2>
          <i className="bi bi-people me-2"></i>
          Daftar User & Status Online
        </h2>
        <div className="users-grid">
          {users.map((u) => (
            <div key={u.id} className={`user-card ${u.is_online ? 'online' : 'offline'}`}>
              <div className="user-status-indicator">
                <span className={`status-dot ${u.is_online ? 'online' : 'offline'}`}></span>
              </div>
              <div className="user-info">
                <h3>
                  <i className="bi bi-person-circle me-2"></i>
                  {u.nama}
                </h3>
                <p className="user-role">
                  <i className={`bi ${u.role === 'admin' ? 'bi-shield-fill-check' : 'bi-truck'} me-1`}></i>
                  {u.role}
                </p>
                <p className="user-phone">
                  <i className="bi bi-telephone me-1"></i>
                  {u.no_telp}
                </p>
                <p className="user-status">
                  {u.is_online ? (
                    <span className="status-online">
                      <i className="bi bi-circle-fill me-1"></i>
                      Online
                    </span>
                  ) : (
                    <span className="status-offline">
                      <i className="bi bi-circle-fill me-1"></i>
                      Terakhir dilihat: {formatLastSeen(u.last_seen)}
                    </span>
                  )}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </>
  );

  return (
    <>
      <Header user={user} onMenuToggle={handleToggleSidebar} />
      <div className="dashboard-layout" style={{ marginTop: '60px' }}>
        <Sidebar 
          user={user} 
          onLogout={onLogout}
          currentPage={getCurrentPage()}
          onNavigate={handleNavigate}
          isOpen={sidebarOpen}
          onToggle={handleToggleSidebar}
        />
        <div className="dashboard-main">
          <div className="dashboard-container">
            {renderContent()}
          </div>
        </div>
      </div>
    </>
  );
}

export default Dashboard;